import { useState, type FormEvent } from 'react';
import type { WithholdingPayableReportResponse } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import { formatMoney } from './money';

interface Props {
  companyId: string;
  currency: string;
  defaultFrom: string;
  defaultTo: string;
  onSessionLost: () => void;
}

/**
 * ضرائب الخصم المستحقة للتوريد فى فترة — ما خصمناه من الموردين من سطور حساب «ضرائب خصم
 * مستحقة» فى الدفتر نفسه، بإجمالى لكل مورد ورقمه الضريبى ونسبة ووعاء كل سطر، لإعداد
 * نموذج التوريد. لا يُحسب هنا شىء غير ما رُحّل.
 */
export default function WithholdingPayableReport({
  companyId,
  currency,
  defaultFrom,
  defaultTo,
  onSessionLost,
}: Props) {
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [report, setReport] = useState<WithholdingPayableReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function load(event: FormEvent): Promise<void> {
    event.preventDefault();
    setLoading(true);
    setFailure(null);
    try {
      setReport(await api.withholdingPayableReport(companyId, from, to));
    } catch (caught) {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      setFailure(
        caught instanceof ProblemError
          ? (caught.problem.detail ?? caught.problem.title)
          : caught instanceof Error
            ? caught.message
            : String(caught),
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="panel">
      <h2>ضرائب خصم مستحقة للتوريد</h2>
      <form className="form-row" onSubmit={(event) => void load(event)}>
        <label>
          من
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>
          إلى
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
        <div className="form-actions">
          <button type="submit" className="ghost" disabled={loading || !from || !to}>
            {loading ? 'جارى العرض…' : 'عرض'}
          </button>
        </div>
      </form>
      {failure && <p className="error">{failure}</p>}
      {report && (
        <>
          <p className="sub">
            حساب {report.accountCode} • افتتاحى {formatMoney(report.openingBalance)} • خُصم فى
            الفترة {formatMoney(report.totalCredit)} • ورِّد {formatMoney(report.totalDebit)} •
            المستحق للمصلحة {formatMoney(report.closingBalance)} {currency}
          </p>
          {report.lines.length === 0 ? (
            <p className="sub">لا خصم من المنبع على الموردين فى هذه الفترة.</p>
          ) : (
            <>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>المورد</th>
                      <th>الرقم الضريبى</th>
                      <th>خُصم</th>
                      <th>ورِّد</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.bySupplier.map((row) => (
                      <tr key={row.supplierId ?? 'none'}>
                        <td>
                          {row.nameAr ? row.code + ' — ' + row.nameAr : 'بلا مورد (قيد يدوى)'}
                        </td>
                        <td className="mono">{row.taxRegistrationNumber ?? ''}</td>
                        <td className="num-cell">{formatMoney(row.credit)}</td>
                        <td className="num-cell">{formatMoney(row.debit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>التاريخ</th>
                      <th>القيد</th>
                      <th>المرجع</th>
                      <th>المورد</th>
                      <th>النسبة %</th>
                      <th>الوعاء</th>
                      <th>خُصم</th>
                      <th>ورِّد</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.lines.map((line, index) => (
                      <tr key={line.journalId + ':' + index}>
                        <td>{line.postingDate}</td>
                        <td className="mono">{line.journalNumber}</td>
                        <td className="mono">{line.reference ?? ''}</td>
                        <td>{line.supplierName ?? ''}</td>
                        <td className="num-cell">{line.ratePercent ?? ''}</td>
                        <td className="num-cell">{line.base ? formatMoney(line.base) : ''}</td>
                        <td className="num-cell">{formatMoney(line.credit)}</td>
                        <td className="num-cell">{formatMoney(line.debit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
